import React, { useState } from "react";
import { http, useAuth } from "@/context/AuthContext";
import { Lock, Eye, EyeOff, CheckCircle, Loader2, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

export default function AdminProfile() {
  const { user } = useAuth();
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setSuccess(false);

    if (newPw.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (newPw !== confirmPw) {
      toast.error("New password and confirmation don't match.");
      return;
    }
    if (newPw === currentPw) {
      toast.error("New password must be different from current password.");
      return;
    }

    setSaving(true);
    try {
      await http.post("/auth/change-password", {
        current_password: currentPw,
        new_password: newPw,
      });
      setSuccess(true);
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
      toast.success("Password updated successfully!");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Couldn't update password. Please try again.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  const strength = (() => {
    if (!newPw) return null;
    let score = 0;
    if (newPw.length >= 8) score++;
    if (newPw.length >= 12) score++;
    if (/[A-Z]/.test(newPw)) score++;
    if (/[0-9]/.test(newPw)) score++;
    if (/[^A-Za-z0-9]/.test(newPw)) score++;
    if (score <= 2) return { label: "Weak", color: "bg-red-500", width: "33%" };
    if (score <= 3) return { label: "Good", color: "bg-yellow-500", width: "66%" };
    return { label: "Strong", color: "bg-green-500", width: "100%" };
  })();

  return (
    <div data-testid="admin-profile-page" className="max-w-2xl">
      <h1 className="text-4xl">Profile &amp; Security</h1>
      <p className="mt-2 text-navy/65">Update your account password and view your team profile.</p>

      {/* Account info card */}
      <div className="card mt-8 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center">
            <UserIcon className="w-6 h-6 text-gold" />
          </div>
          <div>
            <div className="font-medium text-navy">
              {user?.first_name || user?.name || "Team member"}{" "}
              {user?.last_name || ""}
            </div>
            <div className="text-sm text-navy/55">{user?.email}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-navy/5">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-navy/45">Role</div>
            <div className="text-sm text-navy mt-0.5 capitalize">{user?.role || "team_member"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-navy/45">Sign-in method</div>
            <div className="text-sm text-navy mt-0.5">
              {user?.auth_provider === "google" ? "Google" : "Email + Password"}
            </div>
          </div>
        </div>
      </div>

      {/* Password change card */}
      <div className="card mt-6 p-6">
        <div className="flex items-center gap-3 mb-2">
          <Lock className="w-5 h-5 text-navy/60" />
          <h2 className="text-xl text-navy m-0">Change Password</h2>
        </div>
        <p className="text-sm text-navy/55 mb-5">
          Pick something only you know — at least 8 characters, with a mix of letters, numbers, and a symbol.
        </p>

        {success && (
          <div
            data-testid="pw-success-banner"
            className="mb-5 p-3 rounded-lg bg-green-50 border border-green-200 flex items-center gap-2 text-sm text-green-800"
          >
            <CheckCircle className="w-4 h-4" />
            Password updated successfully. Use it next time you log in.
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-widest text-navy/55 mb-1.5">
              Current password
            </label>
            <div className="relative">
              <input
                data-testid="pw-current-input"
                type={showCurrent ? "text" : "password"}
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                required
                autoComplete="current-password"
                className="input-field w-full pr-10"
                placeholder="Your current password"
              />
              <button
                type="button"
                onClick={() => setShowCurrent((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-navy/40 hover:text-navy"
                tabIndex={-1}
              >
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-widest text-navy/55 mb-1.5">
              New password
            </label>
            <div className="relative">
              <input
                data-testid="pw-new-input"
                type={showNew ? "text" : "password"}
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                className="input-field w-full pr-10"
                placeholder="At least 8 characters"
              />
              <button
                type="button"
                onClick={() => setShowNew((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-navy/40 hover:text-navy"
                tabIndex={-1}
              >
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {strength && (
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-navy/10 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${strength.color}`}
                    style={{ width: strength.width }}
                  />
                </div>
                <span className="text-xs text-navy/60">{strength.label}</span>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs uppercase tracking-widest text-navy/55 mb-1.5">
              Confirm new password
            </label>
            <input
              data-testid="pw-confirm-input"
              type={showNew ? "text" : "password"}
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              required
              autoComplete="new-password"
              className="input-field w-full"
              placeholder="Type the new password again"
            />
            {confirmPw && newPw && confirmPw !== newPw && (
              <p className="text-xs text-red-600 mt-1.5">Passwords don&apos;t match yet.</p>
            )}
          </div>

          <button
            type="submit"
            disabled={saving || !currentPw || !newPw || !confirmPw}
            data-testid="pw-submit-btn"
            className="btn-primary w-full justify-center"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Updating…
              </>
            ) : (
              <>
                <Lock className="w-4 h-4" /> Update Password
              </>
            )}
          </button>
        </form>
      </div>

      <p className="text-xs text-navy/45 mt-6">
        Need to log out from all devices?{" "}
        <a href="/admin" className="underline">
          Sign out
        </a>{" "}
        and log back in.
      </p>
    </div>
  );
}
